import { useEffect, useRef, useState } from "react";
import { useAuth } from "../../auth/AuthProvider";
import { useNavigate, Link } from "react-router-dom";
import { Controller, useForm } from "react-hook-form";
import { Checkbox, FormControlLabel } from "@mui/material";
import { Moon, Sun } from "lucide-react";
import { useThemeMode } from "../../ThemeModeContext";
import { tokens } from "../../theme";
import TermsModal from "./TermsModal";

// OTP-only login — no password field. Same brand-panel/card layout and
// email→code flow as botriq-admin-fe's Login.jsx, restyled with this app's
// MUI/tokens stack instead of admin-fe's CSS-var theme.
const FEATURES = ["Live robot tracking", "Fleet management", "Delivery control"];

const OTP_LENGTH = 6;
const RESEND_SECONDS = 60;
const OTP_BOX_KEYS = Array.from({ length: OTP_LENGTH }, (_, i) => `otp-digit-${i}`);

// Same shape check as Register.jsx, so "bad email" is caught here with a clear
// message instead of being sent to the OTP endpoint and coming back as a
// generic "Unable to send the OTP" failure.
const EMAIL_RE = /^[^\s@]+@[^\s@.]+(?:\.[^\s@.]+)+$/; //NOSONAR

const LoginPage = () => {
  const [step, setStep] = useState("email"); // "email" | "code"
  const [email, setEmail] = useState("");
  const [digits, setDigits] = useState(Array(OTP_LENGTH).fill(""));
  const [error, setError] = useState("");
  const [secondsLeft, setSecondsLeft] = useState(RESEND_SECONDS);
  const [isChecked, setIsChecked] = useState(false);
  const [termsOpen, setTermsOpen] = useState(false);
  // `loading` from useAuth is AuthProvider-WIDE: it flips for any sendOtp/verifyOtp
  // anywhere, so driving the email form's button from it made that button read
  // "Sending code…" for a request the user had already abandoned from the code step.
  // `sending` is this form's own submit state and is what the button renders.
  const [sending, setSending] = useState(false);
  // `loading` is still used by the CODE step, where it correctly tracks verifyOtp.
  const { sendOtp, verifyOtp, loading } = useAuth();
  const { isDark, toggleMode } = useThemeMode();
  const navigate = useNavigate();
  const { control } = useForm({ defaultValues: { acceptTerms: false } });
  const inputsRef = useRef([]);
  // Controller for the send currently in flight, so leaving the code step can cancel
  // it. Held in a ref because aborting must not itself trigger a render.
  const sendAbortRef = useRef(null);

  // Abort any in-flight send when the page unmounts, so a late response cannot call
  // setState on a component that is gone.
  useEffect(() => () => sendAbortRef.current?.abort(), []);

  useEffect(() => {
    if (step !== "code" || secondsLeft <= 0) return undefined;
    const id = setInterval(() => setSecondsLeft((s) => (s <= 1 ? 0 : s - 1)), 1000);
    return () => clearInterval(id);
  }, [step, secondsLeft]);

  useEffect(() => {
    if (step === "code") inputsRef.current[0]?.focus();
  }, [step]);

  const handleOpenTerms = (event) => {
    event.preventDefault();
    setTermsOpen(true);
  };

  const handleSendOtp = async (e) => {
    e.preventDefault();
    setError("");
    const value = email.trim();
    if (!value) {
      setError("Please enter your email");
      return;
    }
    if (!EMAIL_RE.test(value)) {
      setError("Enter a valid email address");
      return;
    }
    if (!isChecked) {
      setError("Please accept the Terms and Conditions");
      return;
    }
    const controller = new AbortController();
    sendAbortRef.current = controller;
    setSending(true);
    let result;
    try {
      result = await sendOtp(value, { signal: controller.signal });
    } finally {
      // Only clear the shared slot if this send is still the current one; a newer
      // send may have replaced it while this one was in flight.
      if (sendAbortRef.current === controller) sendAbortRef.current = null;
      setSending(false);
    }
    // The user pressed "Use a different email" (or unmounted) while this was in
    // flight. Advancing to the code step here is exactly the bug: it dragged the
    // user back to a screen they had deliberately left. Drop the result silently —
    // no error either, since nothing actually went wrong.
    if (controller.signal.aborted || result?.aborted) return;
    if (!result.success) {
      setError(result.error);
      return;
    }
    setDigits(Array(OTP_LENGTH).fill(""));
    setSecondsLeft(RESEND_SECONDS);
    setStep("code");
  };

  const handleResend = async () => {
    setError("");
    const controller = new AbortController();
    sendAbortRef.current = controller;
    setSending(true);
    let result;
    try {
      result = await sendOtp(email, { signal: controller.signal });
    } finally {
      if (sendAbortRef.current === controller) sendAbortRef.current = null;
      setSending(false);
    }
    // Same guard as handleSendOtp: a resend the user walked away from must not
    // reset the inputs or steal focus on the email form they moved to.
    if (controller.signal.aborted || result?.aborted) return;
    if (!result.success) {
      setError(result.error);
      return;
    }
    setDigits(Array(OTP_LENGTH).fill(""));
    setSecondsLeft(RESEND_SECONDS);
    inputsRef.current[0]?.focus();
  };

  const handleDigitChange = (index, raw) => {
    const value = raw.replace(/\D/g, "");
    if (!value) {
      setDigits((prev) => {
        const next = [...prev];
        next[index] = "";
        return next;
      });
      return;
    }
    setDigits((prev) => {
      const next = [...prev];
      value.split("").forEach((ch, i) => {
        if (index + i < OTP_LENGTH) next[index + i] = ch;
      });
      return next;
    });
    const nextIndex = Math.min(index + value.length, OTP_LENGTH - 1);
    inputsRef.current[nextIndex]?.focus();
  };

  // Paste needs its own handler: `maxLength={1}` makes the browser truncate a
  // pasted "123456" to "1" BEFORE onChange fires, so handleDigitChange's
  // multi-digit spread never sees more than one character. Reading the clipboard
  // directly sidesteps that, and preventDefault stops the truncated value from
  // also landing in the box.
  const handlePaste = (index, e) => {
    const pasted = (e.clipboardData?.getData("text") || "").replace(/\D/g, "");
    if (!pasted) return;
    e.preventDefault();
    setDigits((prev) => {
      const next = [...prev];
      pasted.split("").forEach((ch, i) => {
        if (index + i < OTP_LENGTH) next[index + i] = ch;
      });
      return next;
    });
    // Land on the last box the paste filled, so a short paste leaves the caret
    // where typing should continue rather than jumping to the end.
    const lastFilled = Math.min(index + pasted.length, OTP_LENGTH) - 1;
    inputsRef.current[Math.max(lastFilled, 0)]?.focus();
  };

  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace" && !digits[index] && index > 0) {
      inputsRef.current[index - 1]?.focus();
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    setError("");
    const code = digits.join("");
    if (code.length !== OTP_LENGTH) {
      setError("Please enter the 6-digit code");
      return;
    }
    const result = await verifyOtp(email, code);
    if (result.success) {
      navigate("/dashboard", { replace: true });
      return;
    }
    setError(result.error);
    setDigits(Array(OTP_LENGTH).fill(""));
    inputsRef.current[0]?.focus();
  };

  const mmss = `${Math.floor(secondsLeft / 60)}:${String(secondsLeft % 60).padStart(2, "0")}`;

  return (
    <div className="rp-login">
      {/* ── Left brand panel (blue gradient) ── */}
      <div className="rp-login-brand">
        <div className="rp-brand-tag">
          <img src="/assets/botriq-icon.png" alt="Botriq" />
          <span>User&nbsp;Portal</span>
        </div>

        <div className="rp-brand-tile-wrap">
          <div className="rp-brand-tile">
            <img src="/assets/botriq-icon.png" alt="" />
          </div>
        </div>

        <div className="rp-brand-copy">
          <h2>Your robots, your control panel.</h2>
          <p>
            Track deliveries in real time, dispatch robots, run advertisements and manage your fleet
            — all from one clean, fast portal.
          </p>
          <ul className="rp-brand-features">
            {FEATURES.map((f) => (
              <li key={f}>
                <span className="rp-check">
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M20 6 9 17l-5-5"
                      stroke="currentColor"
                      strokeWidth="1.75"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </span>
                {f}
              </li>
            ))}
          </ul>
        </div>

        <div className="rp-brand-ring" />
      </div>

      {/* ── Right form panel ── */}
      <div className="rp-login-formpane">
        <div className="rp-login-minibrand">
          <img src="/assets/Botriq-logo.png" alt="Botriq" />
          <span>Portal</span>
        </div>

        {/* The two steps render <form className="rp-form"> at the SAME position in this
            same parent, so without distinct keys React reconciles them as one element
            and REUSES the DOM node, swapping props in place instead of unmounting.
            That aliasing is what made "Use a different email" send a second OTP: the
            button lives inside the code form, its onClick calls setStep("email")
            synchronously, and by the time the click's default submit resolved the
            reused node's onSubmit had already been rebound to handleSendOtp — which
            then passed every guard, because `email` and `isChecked` are deliberately
            preserved. Distinct keys force a real unmount/mount, so a click on one
            form's control can never land on the other's submit handler. */}
        {step === "email" ? (
          <form key="email-step" onSubmit={handleSendOtp} className="rp-form">
            <div className="rp-form-head">
              <img src="/assets/Botriq-logo.png" alt="Botriq" className="rp-form-logo" />
              <div className="rp-form-tag">User Portal</div>
              <h1>Welcome back</h1>
              <p>Sign in with a one-time code emailed to you</p>
            </div>

            {error && <div className="rp-error">{error}</div>}

            <div className="rp-field">
              <label htmlFor="rp-email">Email address</label>
              <div className="rp-input-wrap">
                <svg
                  className="rp-input-lead"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.8"
                >
                  <rect x="2" y="4" width="20" height="16" rx="2" />
                  <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
                </svg>
                <input
                  id="rp-email"
                  type="email"
                  name="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  disabled={sending}
                  autoComplete="email"
                  autoFocus
                />
              </div>
            </div>

            <div className="rp-terms">
              <Controller
                name="acceptTerms"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    sx={{
                      m: 0,
                      "& .MuiFormControlLabel-label": { fontSize: 12.5, color: tokens.text2 },
                    }}
                    control={
                      <Checkbox
                        size="small"
                        sx={{
                          p: "4px",
                          color: tokens.text3,
                          "&.Mui-checked": { color: tokens.accent },
                        }}
                        {...field}
                        checked={field.value}
                        onChange={(e) => {
                          field.onChange(e.target.checked);
                          setIsChecked(e.target.checked);
                        }}
                      />
                    }
                    label={
                      <span>
                        I accept the{" "}
                        <a href="/terms" onClick={handleOpenTerms} style={{ color: tokens.accent }}>
                          Terms and Conditions
                        </a>
                      </span>
                    }
                  />
                )}
              />
            </div>

            <TermsModal open={termsOpen} handleClose={() => setTermsOpen(false)} />

            <button type="submit" className="rp-btn" disabled={sending || !isChecked}>
              {sending ? "Sending code…" : "Send OTP"}
            </button>

            <button type="button" onClick={toggleMode} className="rp-theme-toggle">
              {isDark ? <Sun size={13} /> : <Moon size={13} />}
              Switch to {isDark ? "light" : "dark"}
            </button>

            <div className="rp-form-foot">
              Don&apos;t have an account?{" "}
              <Link to="/register" className="rp-portal-link">
                Register
              </Link>
            </div>
          </form>
        ) : (
          <form key="code-step" onSubmit={handleVerify} className="rp-form">
            <div className="rp-form-head">
              <img src="/assets/Botriq-logo.png" alt="Botriq" className="rp-form-logo" />
              <div className="rp-form-tag">User Portal</div>
              <h1>Enter code</h1>
              <p>
                Sent to <span className="rp-otp-email">{email}</span>
              </p>
            </div>

            {error && <div className="rp-error">{error}</div>}

            <div className="rp-otp-boxes">
              {digits.map((digit, i) => (
                <input
                  key={OTP_BOX_KEYS[i]}
                  ref={(el) => {
                    inputsRef.current[i] = el;
                  }}
                  className="rp-otp-box"
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handleDigitChange(i, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(i, e)}
                  onPaste={(e) => handlePaste(i, e)}
                  disabled={loading}
                  aria-label={`Digit ${i + 1}`}
                />
              ))}
            </div>

            <button type="submit" className="rp-btn" disabled={loading}>
              {loading ? "Verifying…" : "Verify"}
            </button>

            <div className="rp-otp-resend">
              {secondsLeft > 0 ? (
                <span className="rp-otp-resend-txt">Resend code in {mmss}</span>
              ) : (
                <button
                  type="button"
                  className="rp-portal-link rp-otp-resend-btn"
                  onClick={handleResend}
                >
                  Resend code
                </button>
              )}
            </div>

            <button
              type="button"
              className="rp-theme-toggle"
              onClick={(e) => {
                // Belt and braces alongside the distinct form keys above: stop this
                // click from propagating to the form at all, so it cannot be seen as
                // a submit by whichever handler is bound after the step change.
                e.preventDefault();
                e.stopPropagation();
                // Cancel a send that IS genuinely in flight (user hit Resend, then
                // immediately went back) so its continuation cannot call setStep("code").
                sendAbortRef.current?.abort();
                sendAbortRef.current = null;
                setSending(false);
                setStep("email");
                setError("");
                // `email`, `isChecked` and `secondsLeft` are intentionally PRESERVED:
                // the user comes back to edit the address they already typed, and the
                // resend countdown continues from wherever it was.
              }}
            >
              <svg
                width="13"
                height="13"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.75"
              >
                <line x1="19" y1="12" x2="5" y2="12" />
                <polyline points="12 19 5 12 12 5" />
              </svg>
              Use a different email
            </button>
          </form>
        )}
      </div>

      <style>{`
        .rp-login {
          min-height: 100vh;
          display: grid;
          grid-template-columns: 1fr 1fr;
          background: ${tokens.bg};
          color: ${tokens.text};
          font-family: 'Inter', system-ui, sans-serif;
        }

        /* ── Brand panel ── */
        .rp-login-brand {
          position: relative;
          overflow: hidden;
          padding: 40px 56px 56px;
          display: flex;
          flex-direction: column;
          background: linear-gradient(160deg, #3d6ae0 0%, #4f7bf0 55%, #6a8dfc 100%);
          color: #fff;
        }
        .rp-brand-tag {
          display: inline-flex; align-items: center; gap: 9px;
          align-self: flex-start;
          background: rgba(255,255,255,.14);
          border: 1px solid rgba(255,255,255,.22);
          border-radius: 999px;
          padding: 6px 14px 6px 8px;
          backdrop-filter: blur(4px);
          z-index: 1;
        }
        .rp-brand-tag img { height: 20px; width: auto; border-radius: 5px; }
        .rp-brand-tag span {
          font-size: 11px; font-weight: 700; letter-spacing: .1em; text-transform: uppercase;
        }

        .rp-brand-tile-wrap {
          flex: 1; display: grid; place-items: center; z-index: 1;
        }
        .rp-brand-tile {
          width: 190px; height: 190px; border-radius: 40px;
          background: rgba(255,255,255,.16);
          border: 1px solid rgba(255,255,255,.22);
          display: grid; place-items: center;
          box-shadow: 0 24px 60px -18px rgba(0,0,0,.35);
        }
        .rp-brand-tile img {
          width: 108px; height: 108px; object-fit: contain;
          filter: brightness(0) invert(1);
        }

        .rp-brand-copy { max-width: 440px; z-index: 1; }
        .rp-brand-copy h2 {
          margin: 0; font-size: 30px; font-weight: 800; letter-spacing: -.03em; line-height: 1.15;
        }
        .rp-brand-copy p {
          margin: 14px 0 0; font-size: 14px; line-height: 1.6; color: rgba(255,255,255,.82);
        }
        .rp-brand-features {
          list-style: none; margin: 26px 0 0; padding: 0;
          display: flex; flex-direction: column; gap: 12px;
        }
        .rp-brand-features li {
          display: flex; align-items: center; gap: 12px;
          font-size: 14px; font-weight: 600;
        }
        .rp-check {
          width: 26px; height: 26px; border-radius: 8px; flex: none;
          display: grid; place-items: center;
          background: rgba(255,255,255,.18);
          border: 1px solid rgba(255,255,255,.28);
        }
        .rp-brand-ring {
          position: absolute; right: -120px; top: -120px;
          width: 420px; height: 420px; border-radius: 50%;
          border: 1.5px solid rgba(255,255,255,.16);
          pointer-events: none;
        }

        /* ── Form pane ── */
        .rp-login-formpane {
          display: grid; place-items: center; padding: 32px; background: ${tokens.bg};
        }
        .rp-login-minibrand {
          display: none; align-items: center; gap: 10px; margin-bottom: 24px;
        }
        .rp-login-minibrand img { height: 26px; width: auto; }
        .rp-login-minibrand span {
          font-size: 10px; font-weight: 700; letter-spacing: .08em; text-transform: uppercase;
          color: ${tokens.accent}; background: ${tokens.accentSoft}; border-radius: 6px; padding: 3px 8px;
        }

        .rp-form {
          width: 100%; max-width: 400px;
          background: ${tokens.surface};
          border: 1px solid ${tokens.border};
          border-radius: 18px;
          padding: 34px 36px 30px;
          box-shadow: ${tokens.shadowLg};
          display: flex; flex-direction: column;
        }
        .rp-form-head { text-align: center; margin-bottom: 22px; }
        .rp-form-logo { height: 30px; width: auto; object-fit: contain; }
        .rp-form-tag {
          margin-top: 12px; font-size: 10.5px; font-weight: 700; letter-spacing: .1em;
          text-transform: uppercase; color: ${tokens.accent};
        }
        .rp-form-head h1 {
          margin: 8px 0 0; font-size: 24px; font-weight: 800; letter-spacing: -.02em; color: ${tokens.text};
        }
        .rp-form-head p { margin: 6px 0 0; font-size: 13.5px; color: ${tokens.text2}; }
        .rp-otp-email { color: ${tokens.accent}; font-weight: 600; }

        .rp-error {
          background: ${tokens.dangerSoft}; border: 1px solid ${tokens.danger}; color: ${tokens.danger};
          padding: 9px 12px; border-radius: 10px; font-size: 13px; text-align: center;
          margin-bottom: 16px;
        }

        /* Notched outlined field: label sits on the top border */
        .rp-field { position: relative; margin-bottom: 18px; }
        .rp-field label {
          position: absolute; top: -7px; left: 12px; z-index: 1;
          padding: 0 6px; background: ${tokens.surface};
          font-size: 11.5px; font-weight: 500; color: ${tokens.text2};
        }
        .rp-input-wrap { position: relative; display: flex; align-items: center; }
        .rp-input-lead {
          position: absolute; left: 13px; color: ${tokens.text3}; pointer-events: none;
        }
        .rp-input-wrap input {
          width: 100%; height: 52px;
          padding: 0 44px;
          border-radius: 12px;
          border: 1px solid ${tokens.borderStrong};
          background: transparent;
          color: ${tokens.text};
          font-size: 14px; font-family: inherit;
          outline: none;
          transition: border-color .15s, box-shadow .15s;
        }
        .rp-input-wrap input::placeholder { color: ${tokens.text3}; }
        .rp-input-wrap input:focus {
          border-color: ${tokens.accent};
          box-shadow: 0 0 0 3px ${tokens.accentSoft};
        }

        /* 6-box OTP code input */
        .rp-otp-boxes { display: flex; gap: 9px; justify-content: space-between; margin-bottom: 20px; }
        .rp-otp-box {
          width: 100%; aspect-ratio: 1 / 1; max-width: 52px; text-align: center;
          border-radius: 12px;
          border: 1px solid ${tokens.borderStrong};
          background: transparent;
          color: ${tokens.text};
          font-size: 22px; font-weight: 700; font-family: inherit;
          outline: none;
          transition: border-color .15s, box-shadow .15s;
        }
        .rp-otp-box:focus {
          border-color: ${tokens.accent};
          box-shadow: 0 0 0 3px ${tokens.accentSoft};
        }
        .rp-otp-box:disabled { opacity: 0.5; cursor: not-allowed; }

        .rp-otp-resend {
          display: flex; align-items: center; justify-content: center;
          margin-top: 16px; font-size: 12.5px;
        }
        .rp-otp-resend-txt { color: ${tokens.text3}; }
        .rp-otp-resend-btn { background: none; border: none; cursor: pointer; font-family: inherit; font-size: 12.5px; }

        .rp-terms { margin: -2px 0 14px; }

        .rp-btn {
          height: 48px; font-size: 15px; border-radius: 12px;
          border: none; cursor: pointer; font-family: inherit; font-weight: 600;
          color: #fff; background: ${tokens.accent};
          transition: opacity .15s, box-shadow .15s;
        }
        .rp-btn:hover:not(:disabled) { opacity: .92; }
        .rp-btn:disabled { opacity: .55; cursor: not-allowed; }

        .rp-theme-toggle {
          margin: 18px auto 0;
          font-size: 12.5px; color: ${tokens.text3};
          background: none; border: none; cursor: pointer;
          display: inline-flex; align-items: center; gap: 6px;
          padding-top: 16px; border-top: 1px solid ${tokens.border}; width: 100%; justify-content: center;
        }
        .rp-theme-toggle:hover { color: ${tokens.text2}; }

        .rp-form-foot {
          margin-top: 14px; text-align: center;
          font-size: 12.5px; color: ${tokens.text3};
        }
        .rp-portal-link { color: ${tokens.accent}; font-weight: 600; text-decoration: none; }
        .rp-portal-link:hover { text-decoration: underline; }

        /* Collapse to single column on small screens */
        @media (max-width: 860px) {
          .rp-login { grid-template-columns: 1fr; }
          .rp-login-brand { display: none; }
          .rp-login-minibrand { display: flex; }
        }
      `}</style>
    </div>
  );
};

export default LoginPage;
